import { Box, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { color, EL_COLORS } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { FolderEmptyIcon } from '@/UI/Icons/FolderEmptyIcon';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { useIsHovered } from '@/hooks/useIsHovered';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material';

interface FolderCardDisplayProps {
  id: string;
  name: string;
  posters: string[];
  totalFiles: number;
  fileCount?: number;
  subtitle?: ReactNode;
  nameContent?: ReactNode;
  hoverOverlay?: ReactNode;
  forceShowOverlay?: boolean;
  thumbnailSx?: SxProps<Theme>;
}

export function FolderCardDisplay({
  id,
  name,
  posters,
  totalFiles,
  fileCount,
  subtitle,
  nameContent,
  hoverOverlay,
  forceShowOverlay,
  thumbnailSx,
}: FolderCardDisplayProps) {
  const { t } = useTranslation();
  const { isHovered, ...hoveredProps } = useIsHovered();
  const extraCount = totalFiles > 4 ? totalFiles - 4 : 0;

  return (
    <>
      <Box
        data-testid={`body-folder-card-${id}`}
        {...hoveredProps}
        sx={[
          {
            width: '100%',
            height: 240,
            bgcolor: color.Black92,
            borderRadius: 2,
            position: 'relative',
            border: '1px solid',
            borderColor: color.White08_T,
            overflow: 'hidden',
          },
          ...(Array.isArray(thumbnailSx) ? thumbnailSx : thumbnailSx ? [thumbnailSx] : []),
        ]}
      >
        {posters.length > 0 ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: 'repeat(2, 1fr)',
                gap: 1.5,
                padding: 2,
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              {[0, 1, 2, 3].map((index) => {
                const image = posters[index];
                const isLastSlot = index === 3;
                const shouldShowOverlay = isLastSlot && extraCount > 0;

                if (!image) {
                  return <Box key={index} />;
                }

                if (image === 'folder') {
                  return (
                    <FlexCenHorVer
                      key={index}
                      sx={{
                        borderRadius: 2,
                        bgcolor: color.Black92,
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        border: `1px solid ${EL_COLORS.DividerBorder}`,
                      }}
                    >
                      <Box sx={{ opacity: shouldShowOverlay ? 0.5 : 1 }}>
                        <FolderEmptyIcon width={32} height={32} color={color.White80_T} />
                      </Box>
                      {shouldShowOverlay && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: 2,
                            bgcolor: color.Black64_T,
                          }}
                        />
                      )}
                    </FlexCenHorVer>
                  );
                }

                return (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      border: `1px solid ${EL_COLORS.DividerBorder}`,
                    }}
                  >
                    <img
                      src={image}
                      alt="workflow poster"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                    />
                    {shouldShowOverlay && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          borderRadius: 2,
                          backgroundImage: `linear-gradient(90deg, ${color.Black64_T} 0%, ${color.Black64_T} 100%)`,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {extraCount > 0 && (
                <FlexCenHorVer
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    color: color.White100,
                    height: 'calc(50% + 10px)',
                    width: 'calc(50% + 12px)',
                  }}
                >
                  +{extraCount}
                </FlexCenHorVer>
              )}
            </Box>
            {isHovered && !hoverOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: color.White08_T,
                  pointerEvents: 'none',
                }}
              />
            )}
            {(isHovered || forceShowOverlay) && hoverOverlay}
          </>
        ) : (
          <>
            <FlexCenHorVer sx={{ width: '100%', height: '100%' }}>
              <img src="/icons/folder.svg" alt="folder" style={{ width: '42px', height: '42px' }} />
            </FlexCenHorVer>
            {isHovered && !hoverOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: color.White08_T,
                  pointerEvents: 'none',
                }}
              />
            )}
            {(isHovered || forceShowOverlay) && hoverOverlay}
          </>
        )}
      </Box>
      <CardContent
        sx={{
          pt: 1,
          pb: 1,
          pl: 1,
          backgroundColor: 'transparent',
          boxShadow: 'none',
          backgroundImage: 'initial',
        }}
      >
        <FlexCenVer sx={{ gap: 1 }}>
          <FolderEmptyIcon width={24} height={24} color={color.White64_T} />
          <FlexCol sx={{ width: '100%' }}>
            {nameContent ?? <TextStdRg noWrap text={name} color={color.White100} />}
            {subtitle ?? (
              <TextSmRg
                noWrap
                text={
                  (fileCount ?? totalFiles) === 1
                    ? t(I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.FILE_COUNT_SINGULAR, { count: fileCount ?? totalFiles })
                    : t(I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.FILE_COUNT_PLURAL, { count: fileCount ?? totalFiles })
                }
                color={color.White64_T}
              />
            )}
          </FlexCol>
        </FlexCenVer>
      </CardContent>
    </>
  );
}
